/**
 * AdminUserInsights — /admin/user-insights
 *
 * Combined roll-up of every non-admin user's FreedrIQ score band +
 * profile completion %. Filterable (all / incomplete-profile /
 * no-freedriq / both) with an email/name search box + CSV export.
 *
 * Backing endpoints:
 *   GET  /api/admin/user-insights?filter_kind=&q=
 *   GET  /api/admin/user-insights.csv?filter_kind=
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Download, RefreshCw, Users, ClipboardList, Sparkles } from 'lucide-react';

const FILTER_OPTIONS = [
  { v: 'all',                 l: 'All users' },
  { v: 'incomplete_profile',  l: 'Incomplete profile' },
  { v: 'no_freedriq',         l: 'No FreedrIQ' },
  { v: 'both',                l: 'Missing both' },
];

const scoreBand = (s) => {
  if (s == null) return 'text-muted-foreground';
  if (s >= 85) return 'text-emerald-600';
  if (s >= 70) return 'text-primary';
  if (s >= 50) return 'text-amber-600';
  return 'text-rose-600';
};

const AdminUserInsights = () => {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filter !== 'all') params.filter_kind = filter;
      if (q.trim())         params.q = q.trim();
      const { data } = await axios.get(`${API}/admin/user-insights`, { params });
      setRows(data?.rows || []);
      setTotalUsers(data?.total_users || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load user insights');
    } finally {
      setLoading(false);
    }
  }, [API, filter, q]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const url = `${API}/admin/user-insights.csv${filter !== 'all' ? `?filter_kind=${filter}` : ''}`;
    const tk = window.localStorage.getItem('token');
    // Fetch via axios to include auth header, then trigger a client-side download.
    axios.get(url, { responseType: 'blob', headers: tk ? { Authorization: `Bearer ${tk}` } : {} })
      .then((res) => {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `user-insights-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => toast.error('CSV export failed'));
  };

  const totalIncomplete = rows.filter((r) => !r.profile_complete).length;
  const totalNoFreedriq = rows.filter((r) => !r.freedriq_started).length;

  return (
    <div className="container mx-auto px-6 py-8" data-testid="admin-user-insights">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" /> User Insights
              </CardTitle>
              <CardDescription>
                FreedrIQ scores + profile completeness for every non-admin user.
                Filter, search, and export to CSV.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="user-insights-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button size="sm" onClick={exportCsv} data-testid="user-insights-export">
                <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Totals row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-center" data-testid="user-insights-total">
              <div className="text-lg font-bold">{totalUsers}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total users</div>
            </div>
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-center">
              <div className="text-lg font-bold">{rows.length}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Shown</div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-center">
              <div className="text-lg font-bold text-amber-800 dark:text-amber-200">{totalIncomplete}</div>
              <div className="text-[10px] uppercase tracking-widest text-amber-800 dark:text-amber-200">Incomplete profile</div>
            </div>
            <div className="rounded-md border border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-900/20 px-3 py-2 text-center">
              <div className="text-lg font-bold text-rose-800 dark:text-rose-200">{totalNoFreedriq}</div>
              <div className="text-[10px] uppercase tracking-widest text-rose-800 dark:text-rose-200">No FreedrIQ</div>
            </div>
          </div>

          {/* Filter + search bar */}
          <div className="flex flex-wrap items-center gap-2">
            {FILTER_OPTIONS.map((f) => (
              <button
                key={f.v}
                type="button"
                onClick={() => setFilter(f.v)}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                  filter === f.v
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-primary'
                }`}
                data-testid={`user-insights-filter-${f.v}`}
              >
                {f.l}
              </button>
            ))}
            <div className="flex-1" />
            <Input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or name…"
              className="w-full md:w-64 h-9 text-sm"
              data-testid="user-insights-search"
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">User</th>
                  <th className="px-3 py-2 font-semibold">Tier</th>
                  <th className="px-3 py-2 font-semibold">
                    <span className="inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Profile</span>
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> FreedrIQ</span>
                  </th>
                  <th className="px-3 py-2 font-semibold">Missing fields</th>
                </tr>
              </thead>
              <tbody data-testid="user-insights-tbody">
                {rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-border" data-testid={`user-insights-row-${r.user_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium truncate max-w-[220px]">{r.name || r.email}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[220px]">{r.email}</div>
                    </td>
                    <td className="px-3 py-2 uppercase text-xs">{r.tier || 'free'}</td>
                    <td className="px-3 py-2">
                      {r.profile_complete ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                          ✓ {Math.round(r.profile_percentage)}%
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                          {Math.round(r.profile_percentage)}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.freedriq_started ? (
                        <span className={`font-semibold ${scoreBand(r.freedriq_last_score)}`}>
                          {r.freedriq_last_score != null ? Math.round(r.freedriq_last_score) : '—'}
                          <span className="ml-1 text-[10px] text-muted-foreground uppercase">
                            {r.freedriq_last_phase || ''}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-rose-600 font-semibold">Not started</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[280px] truncate">
                      {(r.profile_missing_fields || []).slice(0, 4).join(', ')}
                      {(r.profile_missing_fields || []).length > 4 ? ` +${r.profile_missing_fields.length - 4}` : ''}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-muted-foreground text-sm" data-testid="user-insights-empty">
                    {loading ? 'Loading…' : 'No matching users.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUserInsights;
